import { provide } from '@lit/context'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { filter, fromEvent, map, merge, takeUntil, tap } from 'rxjs'
import { normalizeHex } from './color'
import { themeContext } from './context'
import { formatTheme } from './theme.format'
import { TSchmancyTheme } from './theme.interface'
import { ThemeHereIAm, ThemeWhereAreYou, ThemeWhereAreYouEvent } from './theme.events'
import { theme as themeService } from './theme.service'
import style from './theme.style.css?inline'
import '@schmancy/primitives/surface/surface'
const tailwindStyles = unsafeCSS(style)
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
// A binding like `.color=${maybeUndefined}` coerces to the string "undefined" going into
// sessionStorage; this pattern is the single validity gate at every read/write/registerTheme site.
const HEX_COLOR_PATTERN = /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
// Blocked site storage throws on any access — in Chrome from the
// window.sessionStorage getter itself — and the store is only a preference
// cache, so a throw reads as nothing stored and a write is dropped.
const session = <T>(use: (storage: Storage) => T): T | null => {
	try {
		return use(window.sessionStorage)
	} catch {
		return null
	}
}

@customElement('schmancy-theme')
export class SchmancyThemeComponent extends SchmancyElement {
	static styles = [
		tailwindStyles,
		css`
			:host([scoped]) {
				display: contents;
			}
			/* The root theme fills its parent (the page body, sized to the window)
			 * and hands that height to its surface, so the fill chain runs from the
			 * window down with nothing measuring. Nested (non-root) themes are
			 * untouched — they stay content-sized for scoped sections and cards. */
			:host([root]:not([scoped])) {
				display: flex;
				flex-direction: column;
				flex: 1 1 0;
				min-height: 0;
			}
			:host([root]:not([scoped])) schmancy-surface {
				display: flex;
				flex-direction: column;
				flex: 1 1 0;
				min-height: 0;
			}
		`,
	]

	// Unset → falls back to a stored choice (per DOM-path instance), else a random hex
	// color held in memory only.
	@property({ type: String, reflect: true })
	color: string

	/** Seed used when neither an explicit color nor a stored choice exists. */
	@property({ type: String, attribute: 'default-color' })
	defaultColor?: string

	private schemeValue: 'dark' | 'light' | 'auto'
	private followsSystem = false
	private cachedSeed = ''
	private cachedThemes: Partial<Record<'light' | 'dark', ReturnType<typeof formatTheme>>> = {}
	private appliedTarget?: HTMLElement
	private appliedScheme?: 'dark' | 'light'
	private hostScheme?: 'dark' | 'light'

	// Keep the resolved public value without treating a system resolution as
	// new author intent. An unset value still leaves room for storage precedence.
	@property({ type: String, noAccessor: true })
	get scheme(): 'dark' | 'light' | 'auto' {
		return this.schemeValue
	}
	set scheme(value: 'dark' | 'light' | 'auto') {
		const previous = this.schemeValue
		this.schemeValue = value
		this.followsSystem = value === 'auto'
		this.requestUpdate('scheme', previous)
	}

	@property({ type: Boolean }) root = false

	/** Local palette and context only: no painted surface or global theme controls. */
	@property({ type: Boolean, reflect: true }) scoped = false

	@property({ type: String }) locale: string = typeof navigator !== 'undefined' ? navigator.language : 'de-DE'

	// Falls back to a DOM-path-derived name when unset (see generateThemeName).
	@property({ type: String }) name?: string

	// @internal — populated by registerTheme(); not for external assignment.
	@provide({
		context: themeContext,
	})
	@property({ type: Object })
	theme: Partial<TSchmancyTheme> = {}

	connectedCallback(): void {
		super.connectedCallback()

		const themeName = this.generateThemeName()

		// Precedence: what the author stated — attribute or property binding, both
		// already on this.color/this.scheme by now — > a stored choice > default > random
		// color / 'auto'. The store is keyed by themeName, which every theme on
		// one DOM path shares, so an author who states nothing inherits whatever
		// choice was last stored there. Only `rememberChoice` writes it (§25 TDDDG):
		// a value nobody chose is not stored on every visit.
		const storedColor = session(storage => storage.getItem(`schmancy-theme-${themeName}-color`))
		const storedScheme = session(storage => storage.getItem(`schmancy-theme-${themeName}-scheme`))

		if (storedColor && !HEX_COLOR_PATTERN.test(storedColor)) {
			session(storage => storage.removeItem(`schmancy-theme-${themeName}-color`))
		}

		if (!this.color) {
			this.color = storedColor && HEX_COLOR_PATTERN.test(storedColor)
				? storedColor
				: this.defaultColor && HEX_COLOR_PATTERN.test(this.defaultColor)
					? this.defaultColor
					: this.generateRandomColor()
		}

		if (!this.scheme) {
			this.scheme =
				storedScheme === 'dark' || storedScheme === 'light' || storedScheme === 'auto' ? storedScheme : 'auto'
		}

		// Attach to the service so theme.setScheme/setColor can drive this element live.
		// Synchronous + before the 'auto'→resolved microtask, and attachComponent does not
		// write ThemeContext, so the persisted scheme intent (incl. 'auto') is preserved.
		themeService.attachComponent(this)

		merge(
			fromEvent<MediaQueryListEvent>(mediaQuery, 'change').pipe(
				filter(() => this.followsSystem),
				map((event): 'dark' | 'light' => (event.matches ? 'dark' : 'light')),
				tap(scheme => {
					const previous = this.schemeValue
					this.schemeValue = scheme
					this.requestUpdate('scheme', previous)
				}),
			),
			fromEvent<ThemeWhereAreYouEvent>(this, ThemeWhereAreYou).pipe(
				tap(event => event.stopPropagation()),
				filter(() => !this.scoped),
				tap(event => {
					event.preventDefault()
					window.dispatchEvent(
						new CustomEvent(ThemeHereIAm, {
							detail: { theme: this },
							bubbles: true,
							composed: true,
						}),
					)
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
		this.requestUpdate()
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		themeService.detachComponent(this)
		this.cachedSeed = ''
		this.cachedThemes = {}
		this.appliedTarget = undefined
		this.appliedScheme = undefined
	}

	// `willUpdate`, not `updated`: registerTheme() writes `this.theme`, a
	// reactive property — from `updated()` that set schedules a trailing
	// update (Lit's `change-in-update` warning on every boot, since the
	// color/scheme/locale initializers sit in the first update's own
	// `changedProperties`); from here it folds into the update in flight.
	protected willUpdate(changedProperties: Map<string | number | symbol, unknown>): void {
		super.willUpdate(changedProperties)

		if (this.followsSystem) this.schemeValue = mediaQuery.matches ? 'dark' : 'light'
		if (changedProperties.has('scoped')) {
			if (this.scoped) themeService.detachComponent(this)
			else themeService.attachComponent(this)
		}

		if (
			!this.cachedSeed ||
			changedProperties.has('color') ||
			changedProperties.has('scheme') ||
			changedProperties.has('root') ||
			changedProperties.has('scoped') ||
			changedProperties.has('locale')
		) {
			this.registerTheme(
				changedProperties.has('locale') &&
					!changedProperties.has('color') &&
					!changedProperties.has('scheme') &&
					!changedProperties.has('root') &&
					!changedProperties.has('scoped'),
			)
		}
	}

	/** Stores the current scheme or colour as this theme's restored choice. Called
	 *  only from `theme.setScheme` / `theme.setColor`, the path a person's choice
	 *  takes. @internal */
	rememberChoice(key: 'color' | 'scheme'): void {
		const value = key === 'color' ? this.color : this.scheme
		if (key === 'color' && !HEX_COLOR_PATTERN.test(value)) return
		session(storage => storage.setItem(`schmancy-theme-${this.generateThemeName()}-${key}`, value))
	}

	registerTheme(localeOnly = false) {
		const colorHex = typeof this.color === 'string' && HEX_COLOR_PATTERN.test(this.color) ? this.color : '#6200ee'
		// Unset resolves as 'auto', the same fallback connectedCallback lands on:
		// this method is public, so a call before that resolution must follow the
		// system rather than read as light.
		const scheme = this.scheme ?? 'auto'
		const isDark = this.followsSystem || scheme === 'auto' ? mediaQuery.matches : scheme === 'dark'
		const resolvedScheme = isDark ? 'dark' : 'light'
		const digits = colorHex.startsWith('#') ? colorHex.slice(1) : colorHex
		// Eight-digit input is the established ARGB format, not CSS RGBA.
		const seed = normalizeHex(digits.length === 8 ? digits.slice(2) : digits)
		const hostElement = this.root && !this.scoped ? document.body : this
		const hadCachedTheme = seed === this.cachedSeed && this.cachedThemes[resolvedScheme] !== undefined
		if (seed !== this.cachedSeed) {
			this.cachedSeed = seed
			this.cachedThemes = {}
		}
		const values = (this.cachedThemes[resolvedScheme] ??= formatTheme(seed, isDark))
		if (hostElement !== this && this.hostScheme !== undefined) {
			// Controlled keys must inherit the body's palette after a scope change.
			// Scalar host provenance survives disconnect without retaining colors.
			const clearOwned = (path: string, value: unknown): void => {
				if (value !== null && typeof value === 'object') {
					for (const [key, child] of Object.entries(value)) clearOwned(`${path}${path ? '-' : ''}${key}`, child)
				} else if (typeof value === 'string' || typeof value === 'number') {
					this.style.removeProperty(`--schmancy-${path}`)
				}
			}
			clearOwned('', { ...values, locale: this.locale })
			if (this.style.colorScheme === this.hostScheme) this.style.removeProperty('color-scheme')
			this.hostScheme = undefined
		}
		this.theme = { ...structuredClone(values), locale: this.locale }
		if (localeOnly && hadCachedTheme && this.appliedTarget === hostElement && this.appliedScheme === resolvedScheme) {
			this.registerThemeValues('schmancy', 'locale', this.locale)
			return
		}
		this.registerThemeValues('schmancy', '', { ...values, locale: this.locale })
		if (hostElement.style.colorScheme !== resolvedScheme) hostElement.style.colorScheme = resolvedScheme
		this.appliedTarget = hostElement
		this.appliedScheme = resolvedScheme
		if (hostElement === this) this.hostScheme = resolvedScheme
	}

	registerThemeValues(prefix = 'schmancy', path: string, value: unknown): string | undefined {
		if (value !== null && typeof value === 'object') {
			return Object.entries(value)
				.map(([key, child]) => this.registerThemeValues(prefix, path + `${path ? '-' : ''}` + key, child))
				.join('\n')
		}
		if (typeof value !== 'string' && typeof value !== 'number') return
		const hostElement = this.root && !this.scoped ? document.body : this
		const name = `--${prefix}-${path}`
		const text = String(value)
		if (hostElement.style.getPropertyValue(name) !== text) hostElement.style.setProperty(name, text)
	}
	generateRandomColor() {
		const randomColor = Math.floor(Math.random() * 16777215).toString(16)
		return '#' + randomColor.padStart(6, '0')
	}

	private generateThemeName(): string {
		if (this.name) return this.name

		const path: string[] = []
		let element: Element | null = this

		while (element && element !== document.body) {
			const parent = element.parentElement
			if (parent) {
				const index = Array.from(parent.children).indexOf(element)
				const tag = element.tagName.toLowerCase()
				path.unshift(`${tag}[${index}]`)
			}
			element = parent
		}

		return path.join('>')
	}

	protected render(): unknown {
		if (this.scoped) return html`<slot></slot>`
		return html`
			<schmancy-surface type="containerLowest">
				<slot></slot>
			</schmancy-surface>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-theme': SchmancyThemeComponent
	}
}
